"use client";
import { useSessionContext } from "@/lib/auth/SessionContextProvider";
import { LoginFormSchema } from "@/schema/login";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@radix-ui/react-form";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import Button from "@/components/UI/Button/Button";
import TranslatedLink from "@/components/UI/Link/TranslatedLink";
import { useI18n, useScopedI18n } from "@/i18n/client";
import { useCartContext } from "@/providers/CartContextProvider";
import { DevTool } from "@hookform/devtools";
import { useRouter } from "next/navigation";
import FormField from "../FormField/FormField";
import styles from "./loginForm.module.scss";
import Notification from "@/components/UI/Notification/Notification";
import { useAccountStatusContext } from "@/providers/AccountStatusContextProvider";
import { useDataLayerContext } from "@/datalayer/DataLayerContextProvider";
import { useEffect, useState } from "react";
import CheckboxInput from "@/components/UI/Input/Checkbox/Checkbox";

type FormData = z.infer<typeof LoginFormSchema>;

interface LoginFormProps {
	handleLoginModal?: () => void;
	fullPageLogin?: boolean;
}

export default function LoginForm({
	handleLoginModal,
	fullPageLogin = false,
}: LoginFormProps) {
	const { accountState, setAccountState } = useAccountStatusContext();
	const [isLoading, setIsLoading] = useState(false);

	const { login } = useSessionContext();
	const { setCart } = useCartContext();
	const router = useRouter();
	const t = useI18n();
	const scopedT = useScopedI18n("form.login");

	const {
		handleSubmit,
		register,
		reset,
		formState: { errors, isSubmitting, isValid, isSubmitted },
		control,
	} = useForm<FormData>({
		resolver: zodResolver(LoginFormSchema),
		defaultValues: {
			email: undefined,
			password: undefined,
			rememberMe: true,
		},
	});

	const datalayer = useDataLayerContext();
	const onSubmit = async (data: FormData) => {
		setAccountState({
			status: "idle",
		});
		datalayer.formSubmission("login_form");
		const result = await login(data.email, data.password, data.rememberMe);

		if (result !== undefined) {
			const { cart, url } = result;
			setCart(cart);
			router.push(url);
			return;
		}

		setAccountState({
			status: "error",
			message: scopedT("loginError"),
			isPermanentBanner: true,
		});
	};

	useEffect(() => {
		const hasErrors =
			errors.password || errors.email || accountState.status === "error";

		if (hasErrors) {
			setIsLoading(false);
		} else if (isSubmitting || isSubmitted) {
			setIsLoading(true);
		} else {
			setIsLoading(false);
		}

		// Cleanup to reset loading state on component unmount
		return () => setIsLoading(false);
	}, [errors, accountState, isSubmitting, isSubmitted]);

	return (
		<>
			{fullPageLogin ? (
				<>
					<h1 className={styles.title}>{scopedT("logIn")}</h1>
					{accountState.status === "success" && (
						<Notification type='notification' variant='success' title='Success'>
							{accountState.message}
						</Notification>
					)}
				</>
			) : (
				<h2 className={styles.title}>{scopedT("logIn")}</h2>
			)}

			{accountState.status === "error" && (
				<Notification type='notification' variant='error' title='Error'>
					{accountState.message}
				</Notification>
			)}
			<Form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.fields}>
					<FormField
						{...register("email")}
						required
						label={scopedT("email")}
						placeholder={scopedT("email")}
						disabled={isSubmitting}
						errorMessage={errors.email?.message}
						autoComplete='email'
					/>
					<FormField
						{...register("password")}
						required
						label={scopedT("password")}
						placeholder={scopedT("password")}
						type='password'
						disabled={isSubmitting}
						errorMessage={errors.password?.message}
						autoComplete='current-password'
					/>
					<div className={styles.double}>
						<Controller
							control={control}
							name='rememberMe'
							render={({ field }) => (
								<CheckboxInput
									{...field}
									label={scopedT("rememberMe")}
									value={String(field.value)}
									checked={field.value}
								/>
							)}
						/>

						<TranslatedLink
							href='forgot-password'
							className={styles.forgot}
							onClick={handleLoginModal}
						>
							{scopedT("forgotPassword")}
						</TranslatedLink>
					</div>
				</div>
				<div className={styles.searchButtons}>
					<Button
						as='button'
						type='submit'
						isLoading={isLoading}
						disabled={isSubmitting || !isValid}
					>
						{scopedT("logIn")}
					</Button>
					<Button
						as='button'
						type='button'
						variant='tertiary'
						onClick={() => reset()}
					>
						{t("search.reset")}
					</Button>
				</div>
				{!fullPageLogin && (
					<div className={styles.create}>
						{scopedT("newToUs")}{" "}
						<TranslatedLink
							href='create-account'
							onClick={handleLoginModal}
							underlined={true}
						>
							{scopedT("createAccount")}
						</TranslatedLink>
					</div>
				)}
				{/* <DevTool control={control} /> */}
			</Form>
		</>
	);
}
